import {
  FC, FocusEvent, RefObject, useEffect, useState,
} from 'react';
import { usePlacesWidget } from 'react-google-autocomplete';
import CustomTextField from '../CustomTextField';
import { CONFIG } from '../../config';
import { getPlaceBasedGoogle } from '../../pages/TalentsPage/utils';

export const UNITED_STATES_COUNTRY_CODE = 'US';
export const UNITED_KINGDOM_COUNTRY_CODE = 'UK';
export const FRANCE_COUNTRY_CODE = 'FR';
export const GERMANY_COUNTRY_CODE = 'DE';
export const ISRAEL_COUNTRY_CODE = 'IL';

export const GOOGLE_UNITED_KINGDOM_COUNTRY_CODE = 'GB';

export const AUTOCOMPLETE_ALLOWED_COUNTRIES = [
  UNITED_STATES_COUNTRY_CODE,
  GOOGLE_UNITED_KINGDOM_COUNTRY_CODE,
  FRANCE_COUNTRY_CODE,
  GERMANY_COUNTRY_CODE,
  ISRAEL_COUNTRY_CODE,
];

interface LocationAutocomplete {
  value?: string,
  setLocation: (city: string, state: string, stateLong: string, country: string, countryLong: string, geometry?: {lat: number, lng: number}) => void
  error?: boolean,
  errorText?: string | false | undefined,
  onBlur?: (event: FocusEvent) => void,
  labelTextField: string,
  viewMode: ViewMode,
  resetInputAfterSelect?: boolean,
  disabled: boolean,
}
const LocationAutocomplete:FC<LocationAutocomplete> = (props): JSX.Element => {
  const containerAutocomplete = document.querySelector<HTMLDivElement>('.pac-container');
  const {
    error,
    errorText,
    value,
    onBlur,
    setLocation,
    labelTextField,
    viewMode,
    resetInputAfterSelect,
    disabled,
  } = props;
  const [location, setLocalLocation] = useState(value || '');
  const { ref: autocompleteRef } = usePlacesWidget({
    apiKey: CONFIG.GOOGLE_API_KEY,
    language: 'en',
    options: {
      types: ['(regions)'],
      componentRestrictions: { country: AUTOCOMPLETE_ALLOWED_COUNTRIES },
    },
    onPlaceSelected: async (place: google.maps.places.PlaceResult) => {
      if (place.address_components) {
        const {
          city = '', state = '', stateLong = '', country = '', countryLong = '', geometry,
        } = getPlaceBasedGoogle(place.address_components, place.geometry);
        if (!state) {
          setLocalLocation('');
          
          return;
        }
        await setLocation(city, state, stateLong, country, countryLong, geometry);
        let locationStr = country !== ISRAEL_COUNTRY_CODE ? `${stateLong}, ${country || countryLong}` : countryLong;
        if (city) {
          locationStr = `${city}, ${locationStr}`;
        }
        setLocalLocation(resetInputAfterSelect ? '' : locationStr);
      }
    },
  });

  useEffect(() => {
    if (containerAutocomplete) {
      containerAutocomplete.style.zIndex = '99999';
    }

    const completeRef:RefObject<HTMLInputElement | null> = autocompleteRef;
    if (completeRef && completeRef.current) {
      completeRef.current.autocomplete = 'off';
    }
  },
  [autocompleteRef, containerAutocomplete]);

  useEffect(() => {
    setLocalLocation(value || '');
  },
  [value]);

  return (
    <CustomTextField
      viewMode={ viewMode }
      fullWidth
      label={ labelTextField }
      name="location"
      error={ error }
      helperText={ errorText }
      onBlur={ async (e) => {
        if (onBlur) {
          onBlur(e);
        }
      } }
      onChange={ (e) => {
        setLocalLocation(e.target.value);
      } }
      value={ location }
      inputRef={ autocompleteRef }
      disabled={ disabled }
    />
  );
};

LocationAutocomplete.defaultProps = {
  value: undefined,
  errorText: undefined,
  error: false,
  onBlur: undefined,
  resetInputAfterSelect: false,
};

export default LocationAutocomplete;
